import { supabase } from '@/lib/supabase';
import type { Database } from '@/types/database';
import type { TimerMode } from '@/types/timer';

type Session = Database['public']['Tables']['sessions']['Row'];
type SessionInsert = Database['public']['Tables']['sessions']['Insert'];
type SessionUpdate = Database['public']['Tables']['sessions']['Update'];

export interface SessionService {
  createSession: (
    userId: string,
    mode: TimerMode,
    plannedDuration: number,
    options?: { taskId?: string | null; metadata?: Record<string, unknown> }
  ) => Promise<{ data: Session | null; error: string | null }>;
  updateSession: (sessionId: string, updates: SessionUpdate) => Promise<{ data: Session | null; error: string | null }>;
  endSession: (sessionId: string, actualDuration: number) => Promise<{ data: Session | null; error: string | null }>;
  getActiveSession: (userId: string) => Promise<{ data: Session | null; error: string | null }>;
  getUserSessions: (userId: string, limit?: number) => Promise<{ data: Session[] | null; error: string | null }>;
}

export const sessionService: SessionService = {
  createSession: async (userId: string, mode: TimerMode, plannedDuration: number, options) => {
    try {
      const now = new Date().toISOString();

      const notesPayload: Record<string, unknown> = {};
      if (options?.taskId) {
        notesPayload.taskId = options.taskId;
      }
      if (options?.metadata) {
        Object.assign(notesPayload, options.metadata);
      }
      
      const sessionData: SessionInsert = {
        user_id: userId,
        started_at: now,
        planned_duration: plannedDuration,
        activity_type: mode,
        notes: Object.keys(notesPayload).length > 0 ? JSON.stringify(notesPayload) : null,
      };

      const { data, error } = await supabase
        .from('sessions')
        .insert(sessionData)
        .select()
        .single();

      if (error) {
        console.error('Error creating session:', error);
        return { data: null, error: error.message };
      }

      return { data, error: null };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create session';
      console.error('Error creating session:', err);
      return { data: null, error: errorMessage };
    }
  },

  updateSession: async (sessionId: string, updates: SessionUpdate) => {
    try {
      const { data, error } = await supabase
        .from('sessions')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', sessionId)
        .select()
        .single();

      if (error) {
        console.error('Error updating session:', error);
        return { data: null, error: error.message };
      }

      return { data, error: null };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update session';
      console.error('Error updating session:', err);
      return { data: null, error: errorMessage };
    }
  },

  endSession: async (sessionId: string, actualDuration: number) => {
    try {
      const now = new Date().toISOString();

      console.log('🔴 [sessionService] endSession called:', { sessionId, actualDuration });

      // Direct update (RLS policies fixed in migration 020)
      const { data, error } = await supabase
        .from('sessions')
        .update({
          ended_at: now,
          actual_duration: Math.max(0, actualDuration),
          updated_at: now,
        })
        .eq('id', sessionId)
        .select()
        .single();

      if (error) {
        console.error('🔴 [sessionService] Error ending session:', error);
        return { data: null, error: error.message };
      }

      console.log('✅ [sessionService] Session ended successfully:', data);
      return { data, error: null };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to end session';
      console.error('🔴 [sessionService] Exception ending session:', err);
      return { data: null, error: errorMessage };
    }
  },

  getActiveSession: async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('sessions')
        .select('*')
        .eq('user_id', userId)
        .is('ended_at', null)
        .order('started_at', { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows found
        console.error('Error getting active session:', error);
        return { data: null, error: error.message };
      }

      return { data: data || null, error: null };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to get active session';
      console.error('Error getting active session:', err);
      return { data: null, error: errorMessage };
    }
  },

  getUserSessions: async (userId: string, limit = 50) => {
    try {
      const { data, error } = await supabase
        .from('sessions')
        .select('*')
        .eq('user_id', userId)
        .order('started_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error getting user sessions:', error);
        return { data: null, error: error.message };
      }

      return { data: data || [], error: null };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to get user sessions';
      console.error('Error getting user sessions:', err);
      return { data: null, error: errorMessage };
    }
  },
};
